// ============================================================================
// MarketRequestDetailPage — карточка заявки: статус, детали, фото, отмена и
// переход в чат заявки. Никаких форм платформы: только мобильные блоки.
// ============================================================================

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  CalendarDays,
  Camera,
  Loader2,
  MapPin,
  MessageSquare,
  Phone,
  ShieldAlert,
  RefreshCw,
  ShoppingBag,
  UserRound,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OfflineDataBanner } from '@/components/ui/OfflineDataBanner';
import { FilePreviewModal } from '@/components/viewer/FilePreviewModal';
import { useFileImage } from '@/lib/widgets/fileImage';
import { useToastStore } from '@/store/toastStore';
import { eventBusHub } from '@/lib/eventBusHub';
import { logErrorToJournal } from '@/lib/errors';
import { formatValue } from '@/lib/format';
import {
  isMarketPhoneBlocked,
  loadMarketRequest,
  loadMarketRequestPhotos,
  marketRequestTitle,
  toMarketFailure,
  type MarketFailure,
  type MarketPhoto,
  type MarketRequest,
} from '@/lib/market';
import { cancelMarketRequest, uploadMarketRequestPhotos } from '@/lib/marketActions';
import { MARKET_MAX_PHOTO_BYTES, MARKET_MAX_PHOTOS } from '@/lib/marketConstants';
import { MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketPhoneStatus } from '@/components/market/MarketPhoneStatus';
import { MarketStatusChip, isFinalMarketStatus } from '@/components/market/MarketStatusChip';
import { MarketRatingBadge } from '@/components/market/MarketRatingBadge';
import { MarketRequestReview } from '@/components/market/MarketRequestReview';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { useI18n } from '@/lib/i18n/I18nProvider';

const MAX_PHOTOS = MARKET_MAX_PHOTOS;

interface MarketRequestDetailPageProps {
  requestId: string;
  onBack: () => void;
  onOpenChat: (request: MarketRequest) => void;
  /** Открыть сценарий спора (доступ проверяет экран спора). */
  onOpenDispute?: (request: MarketRequest) => void;
}

function PhotoThumb({ photo, onOpen }: { photo: MarketPhoto; onOpen: () => void }) {
  const { src, error } = useFileImage(photo.id);
  return (
    <button
      type="button"
      onClick={onOpen}
      title={error || photo.fileName}
      className="relative aspect-square overflow-hidden rounded-xl border bg-muted"
    >
      {src ? (
        <img src={src} alt={photo.fileName} className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full items-center justify-center text-muted-foreground">
          {error ? <AlertTriangle className="h-5 w-5" /> : <Loader2 className="h-4 w-4 animate-spin" />}
        </span>
      )}
    </button>
  );
}

function InfoRow({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 py-2.5">
      <span className="mt-0.5 text-muted-foreground">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className="mt-0.5 whitespace-pre-wrap break-words text-sm leading-5">{value}</div>
      </div>
    </div>
  );
}

export function MarketRequestDetailPage({
  requestId,
  onBack,
  onOpenChat,
  onOpenDispute,
}: MarketRequestDetailPageProps) {
  const { t } = useI18n();
  const showSuccess = useToastStore((s) => s.showSuccess);
  const showError = useToastStore((s) => s.showError);
  const [request, setRequest] = useState<MarketRequest | null>(null);
  const [photos, setPhotos] = useState<MarketPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<MarketFailure | null>(null);
  const [photoFailure, setPhotoFailure] = useState<MarketFailure | null>(null);
  const [actionFailure, setActionFailure] = useState<MarketFailure | null>(null);
  const [preview, setPreview] = useState<MarketPhoto | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [tick, setTick] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadPhotos = useCallback(async (id: string) => {
    setPhotoFailure(null);
    try {
      setPhotos(await loadMarketRequestPhotos(id));
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: фото заявки');
      setPhotoFailure(toMarketFailure(e));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setFailure(null);
    setActionFailure(null);
    try {
      const item = await loadMarketRequest(requestId);
      setRequest(item);
      await loadPhotos(requestId);
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: карточка заявки');
      setFailure(toMarketFailure(e));
    } finally {
      setLoading(false);
    }
  }, [loadPhotos, requestId]);

  useEffect(() => {
    void load();
  }, [load, tick]);

  // Живое обновление карточки: выбранный мастер/статус появляются без
  // ручного обновления (событие data.document.mkt_requests.*).
  useEffect(() => {
    return eventBusHub.subscribe((ev) => {
      if (ev.topic.startsWith('data.document.mkt_requests.')) setTick((n) => n + 1);
    });
  }, []);

  const cancel = async () => {
    if (!request) return;
    setCancelling(true);
    setActionFailure(null);
    try {
      await cancelMarketRequest(request.id);
      setRequest((prev) => (prev ? { ...prev, status: 'canceled', statusLabel: t('market.request_canceled') } : prev));
      setConfirmCancel(false);
      showSuccess(t('market.request_canceled'));
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: отмена заявки');
      setActionFailure(toMarketFailure(e));
    } finally {
      setCancelling(false);
    }
  };

  const uploadPhotos = async (files: File[]) => {
    if (!request || files.length === 0) return;
    const free = Math.max(0, MAX_PHOTOS - photos.length);
    if (free === 0) {
      showError(`К заявке можно приложить не больше ${MAX_PHOTOS} фото`);
      return;
    }
    if (files.length > free) {
      showError(`Добавлено только ${free} фото — к заявке можно приложить не больше ${MAX_PHOTOS}`);
    }
    const accepted = files.slice(0, free).filter((file) => {
      if (!file.type.startsWith('image/')) {
        showError(`Файл «${file.name}» не является изображением`);
        return false;
      }
      if (file.size > MARKET_MAX_PHOTO_BYTES) {
        showError(`Фото «${file.name}» больше 15 МБ`);
        return false;
      }
      return true;
    });
    if (accepted.length === 0) return;
    setUploadingPhotos(true);
    setActionFailure(null);
    try {
      const result = await uploadMarketRequestPhotos(request.id, accepted, (done, total) => {
        setUploadProgress(`${done} из ${total}`);
      });
      await loadPhotos(request.id);
      if (result.uploaded > 0) showSuccess(`Фото добавлено: ${result.uploaded}`);
      if (result.queued > 0) {
        showSuccess(`Фото в очереди: ${result.queued} — отправятся при восстановлении связи`);
      }
      if (result.failures.length > 0) {
        showError(result.failures.map((f) => `${f.fileName}: ${f.error}`).join('; '));
      }
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: загрузка фото в карточке');
      setActionFailure(toMarketFailure(e));
    } finally {
      setUploadingPhotos(false);
      setUploadProgress('');
    }
  };

  if (loading && !request) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title={t('market.request')} onBack={onBack} />
        <div className="flex-1 p-4 sm:p-6"><MarketLoading text={t('market.request_loading')} /></div>
      </div>
    );
  }

  if (failure && !request) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title={t('market.request')} onBack={onBack} />
        <div className="mx-auto w-full max-w-2xl flex-1 overflow-y-auto p-4 sm:p-6">
          <MarketError failure={failure} onRetry={() => setTick((n) => n + 1)} />
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="flex h-full flex-col">
        <MarketTopBar title={t('market.request')} onBack={onBack} />
        <div className="mx-auto w-full max-w-2xl flex-1 p-4 sm:p-6">
          <MarketError failure={{ kind: 'other', message: t('market.request_not_found') }} onRetry={() => setTick((n) => n + 1)} />
        </div>
      </div>
    );
  }

  const created = formatValue(request.createdAt, 'datetime:dd.MM.yyyy HH:mm', 'timestamp');
  const canCancel = !isFinalMarketStatus(request.status);
  const title = marketRequestTitle(request);

  return (
    <div className="flex h-full flex-col">
      <MarketTopBar
        title={title}
        subtitle={request.categoryName || t('market.service_request')}
        onBack={onBack}
        action={(
          <Button
            variant="ghost"
            size="sm"
            className="h-8 rounded-lg px-2 text-xs"
            onClick={() => setTick((n) => n + 1)}
            disabled={loading}
            aria-label={t('common.refresh')}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        )}
      />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-2xl space-y-4 p-4 pb-8 sm:p-6">
          <OfflineDataBanner sourceKey={`market:request:${requestId}`} />

          {failure && <MarketError failure={failure} onRetry={() => setTick((n) => n + 1)} />}

          <section className="rounded-3xl border bg-card p-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              <MarketStatusChip status={request.status} label={request.statusLabel} />
              {created && <span className="text-[11px] text-muted-foreground">{created}</span>}
            </div>
            <h1 className="mt-3 whitespace-pre-wrap break-words text-lg font-semibold leading-7">
              {request.descr || t('market.description_missing')}
            </h1>
          </section>

          {actionFailure && <MarketError failure={actionFailure} />}

          {request.status === 'new' && isMarketPhoneBlocked(request) && (
            <MarketPhoneStatus
              state="unverified"
              audience="client"
              hint={request.phoneVerificationHint || undefined}
            />
          )}

          <section className="divide-y rounded-2xl border bg-card px-4">
            <InfoRow icon={<ShoppingBag className="h-4 w-4" />} label={t('market.category')} value={request.categoryName} />
            <InfoRow icon={<MapPin className="h-4 w-4" />} label={t('market.address')} value={request.address} />
            <InfoRow icon={<Phone className="h-4 w-4" />} label={t('market.phone')} value={request.phone} />
            <InfoRow
              icon={<UserRound className="h-4 w-4" />}
              label={t('market.master')}
              value={request.masterName || t('market.master_not_chosen')}
            />
            {request.masterCatalogId && (
              <div className="flex flex-wrap items-center justify-between gap-2 py-2.5">
                <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {t('market.review_title')}
                </span>
                <MarketRatingBadge
                  rating={request.masterRating}
                  count={request.masterReviewsCount}
                  compact
                />
              </div>
            )}
            <InfoRow icon={<CalendarDays className="h-4 w-4" />} label={t('market.created')} value={created} />
          </section>

          {request.status === 'done' && <MarketRequestReview request={request} />}

          <section className="rounded-2xl border bg-card p-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="text-sm font-semibold">{t('market.photos')}</div>
                <div className="text-[11px] text-muted-foreground">{t('market.photos_limit', { count: MAX_PHOTOS })}</div>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 rounded-xl"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhotos || request.status === 'canceled'}
              >
                {uploadingPhotos ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Camera className="mr-1.5 h-4 w-4" />}
                {uploadingPhotos ? t('market.uploading', { progress: uploadProgress }) : t('market.add')}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  void uploadPhotos(Array.from(e.target.files || []));
                  e.target.value = '';
                }}
              />
            </div>

            {photoFailure && (
              <div className="mt-3 rounded-xl border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                {photoFailure.message}
              </div>
            )}

            {photos.length === 0 && !photoFailure ? (
              <div className="mt-3 rounded-xl border border-dashed px-3 py-5 text-center text-xs text-muted-foreground">
                {t('market.no_photos_hint')}
              </div>
            ) : (
              <div className="mt-3 grid grid-cols-3 gap-2">
                {photos.map((photo) => (
                  <PhotoThumb key={photo.id} photo={photo} onOpen={() => setPreview(photo)} />
                ))}
              </div>
            )}
          </section>

          <section className="space-y-2">
            <Button
              className="h-12 w-full rounded-xl"
              onClick={() => onOpenChat(request)}
            >
              <MessageSquare className="mr-2 h-4 w-4" />
              Открыть чат по заявке
            </Button>

            {onOpenDispute && (
              <Button
                variant="outline"
                className="h-11 w-full rounded-xl"
                onClick={() => onOpenDispute(request)}
              >
                <ShieldAlert className="mr-2 h-4 w-4" />
                Спор по заявке
              </Button>
            )}

            {canCancel && !confirmCancel && (
              <Button
                variant="outline"
                className="h-11 w-full rounded-xl text-destructive hover:text-destructive"
                onClick={() => setConfirmCancel(true)}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Отменить заявку
              </Button>
            )}

            {confirmCancel && (
              <div className="rounded-2xl border border-destructive/30 bg-destructive/5 p-3">
                <div className="text-sm font-medium text-destructive">{t('market.cancel_request')}</div>
                <div className="mt-1 text-xs leading-5 text-muted-foreground">
                  Мастера больше не увидят заявку. Это действие нельзя отменить через витрину.
                </div>
                <div className="mt-3 flex gap-2">
                  <Button
                    variant="outline"
                    className="h-9 flex-1 rounded-xl"
                    onClick={() => setConfirmCancel(false)}
                    disabled={cancelling}
                  >
                    Нет
                  </Button>
                  <Button
                    variant="destructive"
                    className="h-9 flex-1 rounded-xl"
                    onClick={() => void cancel()}
                    disabled={cancelling}
                  >
                    {cancelling ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}
                    Да, отменить
                  </Button>
                </div>
              </div>
            )}
          </section>
        </div>
      </div>

      {preview && (
        <FilePreviewModal
          fileId={preview.id}
          fileName={preview.fileName}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
}
